/**
 * Z Logo Loading Animation Component
 * Displays an animated Z logo with a moving spark effect
 */
import React from 'react';
import { Box } from '@mui/material';

interface ZLoaderProps {
  size?: number;
  showText?: boolean;
}

const ZLoader: React.FC<ZLoaderProps> = ({ size = 150, showText = false }) => {
  // Scale factor based on desired size (base size is 500px width)
  const scale = size / 500;
  const height = size * 1.1; // Maintain aspect ratio

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
      }}
    >
      <Box
        sx={{
          width: size,
          height: height,
          position: 'relative',
          '@keyframes glowPulse': {
            '0%, 100%': {
              filter: 'drop-shadow(0 10px 30px rgba(255, 107, 107, 0.3))',
            },
            '50%': {
              filter: 'drop-shadow(0 10px 40px rgba(255, 107, 107, 0.6))',
            },
          },
          animation: 'glowPulse 2s ease-in-out infinite',
        }}
      >
        <svg
          viewBox="0 0 500 550"
          xmlns="http://www.w3.org/2000/svg"
          style={{ width: '100%', height: '100%' }}
        >
          <defs>
            {/* Gradient for the Z */}
            <linearGradient id="zGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" style={{ stopColor: '#FFD54F', stopOpacity: 1 }} />
              <stop offset="25%" style={{ stopColor: '#FFB74D', stopOpacity: 1 }} />
              <stop offset="50%" style={{ stopColor: '#FF9800', stopOpacity: 1 }} />
              <stop offset="75%" style={{ stopColor: '#FF7043', stopOpacity: 1 }} />
              <stop offset="100%" style={{ stopColor: '#F4511E', stopOpacity: 1 }} />
            </linearGradient>

            {/* Highlight gradient */}
            <linearGradient id="highlightGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" style={{ stopColor: '#FFEB3B', stopOpacity: 0.6 }} />
              <stop offset="50%" style={{ stopColor: '#FFD54F', stopOpacity: 0.3 }} />
              <stop offset="100%" style={{ stopColor: '#FFB74D', stopOpacity: 0 }} />
            </linearGradient>

            {/* Path for spark animation: top-left → top-right → diagonal → bottom-left → bottom-right */}
            <path
              id="sparkPath"
              d="
                M 120 212.3
                L 360 124.8
                L 140 404.2
                L 380 291.3
              "
            />
          </defs>

          {/* Z shape with rounded edges, 5% taller vertically, 20° angles */}
          <path
            d="
              M 120 212.3
              L 360 124.8
              L 140 404.2
              L 380 291.3
            "
            stroke="url(#zGradient)"
            strokeWidth="75"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
            filter="drop-shadow(0 10px 30px rgba(255, 107, 107, 0.3))"
          />

          {/* Top highlight (rotated to match slanted top line) */}
          <ellipse
            cx="240"
            cy="168.55"
            rx="130"
            ry="25"
            fill="url(#highlightGradient)"
            opacity="0.7"
            transform="rotate(-20 240 168.55)"
          />

          {/* Bottom highlight (rotated to match slanted bottom line) */}
          <ellipse
            cx="260"
            cy="347.75"
            rx="110"
            ry="22"
            fill="url(#highlightGradient)"
            opacity="0.6"
            transform="rotate(-20 260 347.75)"
          />

          {/* Spark animation */}
          <g>
            {/* Main bright spark */}
            <circle r="35" fill="white">
              <animateMotion dur="2.5s" repeatCount="indefinite">
                <mpath href="#sparkPath" />
              </animateMotion>
              <animate attributeName="r" values="30;42;30" dur="0.5s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0;1;1;1;0" dur="2.5s" repeatCount="indefinite" />
            </circle>

            {/* Outer glow */}
            <circle r="60" fill="white" opacity="0.25">
              <animateMotion dur="2.5s" repeatCount="indefinite">
                <mpath href="#sparkPath" />
              </animateMotion>
              <animate attributeName="r" values="55;70;55" dur="0.5s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0;0.25;0.25;0.25;0" dur="2.5s" repeatCount="indefinite" />
            </circle>

            {/* Inner core */}
            <circle r="15" fill="#FFD54F">
              <animateMotion dur="2.5s" repeatCount="indefinite">
                <mpath href="#sparkPath" />
              </animateMotion>
              <animate attributeName="r" values="12;18;12" dur="0.5s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0;1;0.5;1;0" dur="2.5s" repeatCount="indefinite" />
            </circle>
          </g>
        </svg>
      </Box>

      {showText && (
        <Box
          sx={{
            color: '#FF9800',
            fontSize: 18,
            letterSpacing: 2,
            fontWeight: 500,
            '@keyframes pulse': {
              '0%, 100%': {
                opacity: 0.6,
              },
              '50%': {
                opacity: 1,
              },
            },
            animation: 'pulse 1.5s ease-in-out infinite',
          }}
        >
          LOADING...
        </Box>
      )}
    </Box>
  );
};

export default ZLoader;
